import { useEffect } from "react";
import "./CustomGenericModal.css";
import Modal from "@mui/material/Modal";
import CustomButton from "./CustomButton";
import InfoIconImgSrc from "../../assets/image/png/info.png";
import TickconImgSrc from "../../assets/image/png/tick.png";
import NegativeIconImgSrc from "../../assets/image/png/error.png";
import Fade from "@mui/material/Fade";
import { ReactComponent as PdfIcon } from "../../assets/image/svg/pdfIcon.svg";
import printIcon from "../../assets/image/svg/icon.svg";
import { CircularProgress } from "@mui/material";
import useShortcutKeys from "./ShortcutKeys";
import { Checkbox } from "@mui/material";

interface CustomPopupProps {
  type: "positive" | "success" | "negative" | "error" | "";
  message: string | any[];
  leftButtonText?: string;
  rightButtonText?: string;
  onLeftButtonClick?: () => void;
  onRightButtonClick?: () => void;
  onClose?: () => void;
  modalOpen: boolean;
  loading?: boolean;
  downloadGRNSheetLink?: string;
  onClickDownloadGRNSheetLink?: () => void;
  leftButtonPadding?: string;
  width?: string;
  errorMessages?: boolean;
  errorMessageText?: string;
  addCheckBox?: boolean;
  headerText?: string;
  subHeaderText?: string;
  headerIcon?: {
    headerIconType?: string;
    width?: string;
  };
  selectedCheckboxes?: number[];
  setSelectedCheckboxes?: (selectedCheckboxes: number[]) => void;
}

const customStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  color: "#fff",
  marginTop: "5px",
};

const CustomGenericModal: React.FC<CustomPopupProps> = ({
  type,
  message,
  leftButtonText,
  rightButtonText,
  onLeftButtonClick,
  onRightButtonClick,
  onClose,
  modalOpen,
  loading,
  downloadGRNSheetLink,
  onClickDownloadGRNSheetLink,
  leftButtonPadding,
  width = "450px",
  errorMessages = true,
  errorMessageText = "",
  addCheckBox = false,
  selectedCheckboxes,
  setSelectedCheckboxes,
  headerText = "",
  subHeaderText = "",
  headerIcon = {
    headerIconType: "",
    width: "100%",
  },
}) => {
  // Add keyboard shortcut handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore shortcuts if modal is not open
      if (!modalOpen) return;

      // Ignore shortcuts if user is typing in an input or textarea
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")
      ) {
        return;
      }

      // Check for Ctrl (Windows/Linux) or Meta (Mac) key
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === "a" && onLeftButtonClick && leftButtonText && !loading) {
        event.preventDefault();
        console.log("Ctrl + A pressed: Triggering onLeftButtonClick");
        onLeftButtonClick();
      } else if (key === "x" && onRightButtonClick && rightButtonText) {
        event.preventDefault();
        console.log("Ctrl + X pressed: Triggering onRightButtonClick");
        onRightButtonClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, onLeftButtonClick, onRightButtonClick, leftButtonText, rightButtonText, loading]);

   const { positiveCta, negativeCta } = useShortcutKeys();

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "positive":
        return InfoIconImgSrc;
      case "success":
        return TickconImgSrc;
      case "negative":
        return NegativeIconImgSrc;
      case "error":
        return NegativeIconImgSrc;
      case "print":
        return printIcon;
      default:
        return "";
    }
  };

  return (
    <Modal
      open={modalOpen}
      onClose={onClose}
      closeAfterTransition
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={modalOpen}>
        <div className="CustomGenericModal_popup-container" style={{ width: width }}>
          <div className="CustomGenericModal_popup-content" style={{ width: addCheckBox ? "90%" : "" }}>
            <div className="CustomGenericModal_popup-sign">
              {type && <img src={getTypeIcon(type)} alt={`${type} icon`} />}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "7px",
                fontWeight: 600,
              }}
            >
              {headerIcon && headerIcon.headerIconType && (
                <div className="CustomGenericModal_popup-headerIcon">
                  <img src={getTypeIcon(headerIcon.headerIconType)} alt="header icon" style={{ width: headerIcon?.width }} />
                </div>
              )}
              <div>
                {headerText && (
                  <p className="CustomGenericModal_popup-header">
                    {headerText}
                  </p>
                )}
                {subHeaderText && (
                  <p className="CustomGenericModal_popup-subHeader">
                    {subHeaderText}
                  </p>
                )}
              </div>
            </div>
            <div
              className="CustomGenericModal_popup-message"
              style={{
                margin: addCheckBox ? "17px 0px 17px" : "",
                gap: addCheckBox ? "12px" : "",
                display: addCheckBox ? "grid" : "",
              }}
            >
              {Array.isArray(message) && errorMessages
                ? message.map((error: any, index: number) => {
                    const { ErrorCode, ErrorMessage } = error || {};
                    return (
                      <p
                        key={index}
                        style={{ fontWeight: 500, marginBottom: "10px" }}
                      >
                        {`Error ${ErrorCode}`}:{" "}
                        <span style={{ fontWeight: 450, color: "#707070" }}>
                          {ErrorMessage}
                        </span>
                      </p>
                    );
                  })
                : Array.isArray(message)
                ? message.map((msg: any, index: number) => {
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {addCheckBox && (
                          <Checkbox
                            size="small"
                            sx={{
                              p: 0,
                              "&.Mui-checked": {
                                color: "#00C6B1",
                              },
                            }}
                            checked={selectedCheckboxes?.includes(index)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCheckboxes?.([
                                  ...(selectedCheckboxes || []),
                                  index,
                                ]);
                              } else {
                                setSelectedCheckboxes?.(
                                  (selectedCheckboxes || []).filter(
                                    (item: number) => item !== index
                                  )
                                );
                              }
                            }}
                          />
                        )}
                        <p
                          key={index}
                          className="CustomGenericModal_popup-messageTxt"
                        >
                          {msg}
                        </p>
                      </div>
                    );
                  })
                : typeof message === "string"
                ? message
                : "Some error occurred"}
            </div>
            {errorMessageText && (
              <div className="CustomGenericModal_popup-errorMessage">
                {errorMessageText}
              </div>
            )}
            <div
              className="CustomGenericModal_popup-buttons"
              style={leftButtonPadding ? { padding: leftButtonPadding } : undefined}
            >
              {rightButtonText && onRightButtonClick && (
                <CustomButton
                  onClick={onRightButtonClick}
                  fontColor="#C44C36"
                  backgroundColor="#fff"
                  borderColor="#C44C36"
                  width="120px"
                >
                  {rightButtonText}
                  &nbsp;<span style={{fontSize:"10.5px"}}>{negativeCta}</span>

                </CustomButton>
              )}
              {leftButtonText && onLeftButtonClick && (
                <CustomButton
                  onClick={onLeftButtonClick}
                  width="120px"
                  flexGrow={rightButtonText ? 0 : 1}
                >
                  {loading ? <CircularProgress style={customStyle} /> : leftButtonText}
                    {!loading && <span style={{fontSize:"10.5px"}}>{' ' + positiveCta}</span>}
                </CustomButton>
              )}
            </div>
            <div>
              {downloadGRNSheetLink && onClickDownloadGRNSheetLink && (
                <button
                  style={{ margin: "20px 0px 15px 90px" }}
                  onClick={onClickDownloadGRNSheetLink}
                  className="ViewOrder_printInvoice"
                >
                  <span>Download GRN for Signatures</span> <PdfIcon />
                </button>
              )}
            </div>
          </div>
          {onClose && (
            <button className="CustomGenericModal_popup-close" onClick={onClose}>
              ×
            </button>
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default CustomGenericModal;
